// ─────────────────────────────────────────────────────────────────────────────
//  DomainRenewalInvoice
//
//  The renewal invoice document, rendered two ways from one source of truth:
//
//    <DomainRenewalInvoice data={…} />   on-screen, inside the prompt or the
//                                       admin panel
//    printDomainInvoice(data)           standalone A4 HTML in a new window,
//                                       for printing or "Save as PDF"
//
//  Layout follows the conventions of a registrar invoice — issued-by / billed-to
//  columns, a line-item table, a total block, and the registration period stated
//  explicitly. The document is TotalCare's own: Namecheap is named as the
//  upstream registrar because that is factually where the domain is held, which
//  is the information a facility's accountant actually needs to reconcile it.
//  It is deliberately not dressed up as a Namecheap-issued document — the money
//  is owed to TotalCare, and the invoice should say so.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { Box, Typography, Divider, Chip } from '@mui/material';
import {
  ReceiptLong as ReceiptIcon,
  LanguageOutlined as GlobeIcon,
} from '@mui/icons-material';

/* ── Stage presentation ───────────────────────────────────────────────────────
   Keyed to the `stage` the backend computes, so the ladder lives in one place
   (server) and the UI only decides how it looks.                              */
export const STAGE_META = {
  notice: {
    label:  'Renewal due',
    accent: '#1a6b8a',
    bg:     '#e8f4f8',
    tone:   'Your domain registration is approaching its renewal date.',
  },
  warning: {
    label:  'Renewal due soon',
    accent: '#b45309',
    bg:     '#fef3e2',
    tone:   'Your domain registration expires within a month. Please arrange payment to avoid interruption.',
  },
  urgent: {
    label:  'Action required',
    accent: '#c0392b',
    bg:     '#fdecea',
    tone:   'Your domain expires in days. Once it lapses, staff will be unable to log in until it is renewed.',
  },
  expired: {
    label:  'Expired',
    accent: '#7f1d1d',
    bg:     '#fdecea',
    tone:   'Your domain registration has lapsed. The system is now restricted until renewal is settled.',
  },
  ok: {
    label:  'Active',
    accent: '#2e8b57',
    bg:     '#e9f7ef',
    tone:   'Your domain registration is active.',
  },
};

const CURRENCY_SYMBOLS = { GHS: 'GH¢', USD: '$', GBP: '£', EUR: '€', NGN: '₦' };

export const currencySymbol = (code) => CURRENCY_SYMBOLS[code] || `${code || ''} `;

export const formatMoney = (amount, currency = 'GHS') => {
  if (amount == null || amount === '') return '—';
  const n = Number(amount);
  if (isNaN(n)) return '—';
  return `${currencySymbol(currency)}${n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

/* Plain-English countdown. Reads better than a bare number on an invoice and
   handles the awkward cases (today, tomorrow, already past). */
export const describeDays = (days) => {
  if (days == null)  return '';
  if (days < 0)      return `${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} overdue`;
  if (days === 0)    return 'expires today';
  if (days === 1)    return 'expires tomorrow';
  if (days <= 60)    return `${days} days remaining`;
  return `${days} days remaining`;
};

/* ── On-screen document ─────────────────────────────────────────────────────*/
const DomainRenewalInvoice = ({ data, compact = false }) => {
  if (!data || !data.invoice) return null;

  const inv   = data.invoice;
  const meta  = STAGE_META[data.stage] || STAGE_META.ok;
  const total = inv.renewalAmount;

  const rowSx  = { display: 'flex', justifyContent: 'space-between', py: 0.4, gap: 2 };
  const keySx  = { fontSize: '0.72rem', color: '#64748b', whiteSpace: 'nowrap' };
  const valSx  = { fontSize: '0.78rem', fontWeight: 600, color: '#0f172a', textAlign: 'right' };

  return (
    <Box sx={{
      background: '#fff',
      border: '1px solid #e2e8f0',
      borderRadius: 2,
      overflow: 'hidden',
      maxWidth: 780,
      mx: 'auto',
    }}>
      {/* ── Status strip ─────────────────────────────────────────────── */}
      <Box sx={{
        px: compact ? 2 : 3, py: 1.25,
        background: meta.bg,
        borderBottom: `2px solid ${meta.accent}`,
        display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
      }}>
        <Chip
          size="small"
          label={meta.label}
          sx={{ background: meta.accent, color: '#fff', fontWeight: 700, fontSize: '0.68rem', height: 22 }}
        />
        <Typography sx={{ fontSize: '0.78rem', color: meta.accent, fontWeight: 600 }}>
          {describeDays(data.daysLeft)}
        </Typography>
        <Typography sx={{ fontSize: '0.74rem', color: '#475569', flex: 1, minWidth: 200 }}>
          {meta.tone}
        </Typography>
      </Box>

      {/* ── Masthead ─────────────────────────────────────────────────── */}
      <Box sx={{
        px: compact ? 2 : 3, pt: 2.5, pb: 2,
        display: 'flex', justifyContent: 'space-between', gap: 3, flexWrap: 'wrap',
      }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <ReceiptIcon sx={{ fontSize: 22, color: '#11998e' }} />
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#0f172a', letterSpacing: 0.2 }}>
              TotalCare HMS
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '0.72rem', color: '#64748b', lineHeight: 1.5 }}>
            Hospital Management System<br />
            Domain &amp; Hosting Services
          </Typography>
        </Box>

        <Box sx={{ textAlign: 'right' }}>
          <Typography sx={{
            fontSize: '1.35rem', fontWeight: 800, color: '#0f172a',
            letterSpacing: 3, textTransform: 'uppercase', lineHeight: 1,
          }}>
            Invoice
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#11998e', mt: 0.5 }}>
            {inv.invoiceNo || '—'}
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: '#64748b', mt: 0.25 }}>
            Due by {formatDate(inv.expiresAt)}
          </Typography>
        </Box>
      </Box>

      <Divider />

      {/* ── Billed to / registration ─────────────────────────────────── */}
      <Box sx={{
        px: compact ? 2 : 3, py: 2,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
        gap: 3,
      }}>
        <Box>
          <Typography sx={{
            fontSize: '0.64rem', fontWeight: 800, letterSpacing: 1,
            color: '#94a3b8', textTransform: 'uppercase', mb: 0.75,
          }}>
            Billed to
          </Typography>
          <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: '#0f172a' }}>
            {inv.hospital || '—'}
          </Typography>
          {inv.domainName && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <GlobeIcon sx={{ fontSize: 14, color: '#64748b' }} />
              <Typography sx={{ fontSize: '0.76rem', color: '#475569', fontFamily: 'monospace' }}>
                {inv.domainName}
              </Typography>
            </Box>
          )}
        </Box>

        <Box>
          <Typography sx={{
            fontSize: '0.64rem', fontWeight: 800, letterSpacing: 1,
            color: '#94a3b8', textTransform: 'uppercase', mb: 0.75,
          }}>
            Registration
          </Typography>
          <Box sx={rowSx}>
            <Typography sx={keySx}>Registrar</Typography>
            <Typography sx={valSx}>{inv.registrar || 'Namecheap Inc.'}</Typography>
          </Box>
          <Box sx={rowSx}>
            <Typography sx={keySx}>Registered on</Typography>
            <Typography sx={valSx}>{formatDate(inv.registeredOn)}</Typography>
          </Box>
          <Box sx={rowSx}>
            <Typography sx={keySx}>Paid through</Typography>
            <Typography sx={valSx}>{formatDate(inv.expiresAt)}</Typography>
          </Box>
          {inv.graceUntil && inv.graceUntil !== inv.expiresAt && (
            <Box sx={rowSx}>
              <Typography sx={keySx}>Goes offline</Typography>
              <Typography sx={{ ...valSx, color: '#c0392b' }}>{formatDate(inv.graceUntil)}</Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Divider />

      {/* ── Line items ───────────────────────────────────────────────── */}
      <Box sx={{ px: compact ? 2 : 3, pt: 2 }}>
        <Box sx={{
          display: 'grid', gridTemplateColumns: '1fr 130px',
          pb: 0.75, borderBottom: '2px solid #e2e8f0',
        }}>
          <Typography sx={{ fontSize: '0.64rem', fontWeight: 800, letterSpacing: 1, color: '#94a3b8', textTransform: 'uppercase' }}>
            Description
          </Typography>
          <Typography sx={{ fontSize: '0.64rem', fontWeight: 800, letterSpacing: 1, color: '#94a3b8', textTransform: 'uppercase', textAlign: 'right' }}>
            Amount
          </Typography>
        </Box>

        <Box sx={{
          display: 'grid', gridTemplateColumns: '1fr 130px',
          py: 1.5, borderBottom: '1px solid #f1f5f9', alignItems: 'start',
        }}>
          <Box>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#0f172a' }}>
              Domain registration renewal
              {inv.renewalYears > 1 ? ` — ${inv.renewalYears} years` : ' — 1 year'}
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', color: '#64748b', mt: 0.25 }}>
              {inv.domainName || 'Facility domain'} · held with {inv.registrar || 'Namecheap Inc.'}
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>
              Extends service from {formatDate(inv.expiresAt)}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: '#0f172a', textAlign: 'right' }}>
            {formatMoney(total, inv.currency)}
          </Typography>
        </Box>

        {/* Total */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', py: 1.75 }}>
          <Box sx={{ minWidth: 260 }}>
            <Box sx={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              px: 1.5, py: 1.25, borderRadius: 1.5, background: '#f8fafc',
              border: '1px solid #e2e8f0',
            }}>
              <Typography sx={{ fontSize: '0.76rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Total due
              </Typography>
              <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#11998e' }}>
                {formatMoney(total, inv.currency)}
              </Typography>
            </Box>
            {total == null && (
              <Typography sx={{ fontSize: '0.68rem', color: '#b45309', mt: 0.75, textAlign: 'right' }}>
                Amount not yet set — contact TotalCare for the renewal quotation.
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* ── Payment notes ────────────────────────────────────────────── */}
      {inv.billingNotes && (
        <Box sx={{ mx: compact ? 2 : 3, mb: 2, p: 1.5, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 1.5 }}>
          <Typography sx={{ fontSize: '0.64rem', fontWeight: 800, letterSpacing: 1, color: '#94a3b8', textTransform: 'uppercase', mb: 0.5 }}>
            Payment instructions
          </Typography>
          <Typography sx={{ fontSize: '0.76rem', color: '#334155', whiteSpace: 'pre-wrap' }}>
            {inv.billingNotes}
          </Typography>
        </Box>
      )}

      {/* ── Footer ───────────────────────────────────────────────────── */}
      <Box sx={{ px: compact ? 2 : 3, py: 1.5, background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
        <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', lineHeight: 1.6 }}>
          Issued by TotalCare HMS for domain and hosting services rendered to the facility named above.
          The domain is registered and maintained on the facility's behalf with {inv.registrar || 'Namecheap Inc.'}.
          Renewal must be settled before the paid-through date to avoid interruption of access.
        </Typography>
        {data.acknowledged && data.acknowledgedBy && (
          <Typography sx={{ fontSize: '0.68rem', color: '#2e8b57', fontWeight: 600, mt: 0.75 }}>
            Reviewed by {data.acknowledgedBy}
            {data.acknowledgedAt ? ` on ${formatDate(data.acknowledgedAt)}` : ''}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

/* ── Printable A4 version ───────────────────────────────────────────────────
   Rendered as standalone HTML in a new window rather than printing the React
   tree. Printing the live DOM would drag the whole app's stylesheet into the
   page and fight with MUI's injected styles; a self-contained document prints
   predictably on every browser and doubles as "Save as PDF".                 */
export const printDomainInvoice = (data) => {
  if (!data || !data.invoice) return;

  const inv  = data.invoice;
  const meta = STAGE_META[data.stage] || STAGE_META.ok;
  const esc  = (s) => String(s ?? '—').replace(/[&<>"]/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]
  ));

  const html = `<!doctype html>
<html>
<head>
<meta charset="utf-8">
<title>${esc(inv.invoiceNo || 'Domain Renewal Invoice')}</title>
<style>
  @page { size: A4; margin: 18mm 16mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
         color: #0f172a; margin: 0; font-size: 11pt; }
  .strip { background: ${meta.bg}; border-bottom: 2px solid ${meta.accent};
           padding: 8px 12px; margin-bottom: 22px; }
  .badge { display: inline-block; background: ${meta.accent}; color: #fff;
           font-size: 8pt; font-weight: 700; padding: 2px 8px; border-radius: 10px;
           text-transform: uppercase; letter-spacing: .5px; }
  .strip .days { font-size: 9pt; font-weight: 600; color: ${meta.accent}; margin-left: 8px; }
  .strip .tone { font-size: 9pt; color: #475569; margin-top: 4px; }
  .masthead { display: flex; justify-content: space-between; align-items: flex-start;
              margin-bottom: 18px; }
  .brand { font-size: 15pt; font-weight: 800; }
  .brand small { display: block; font-size: 8.5pt; font-weight: 400; color: #64748b;
                 margin-top: 3px; line-height: 1.5; }
  .title { text-align: right; }
  .title h1 { font-size: 19pt; letter-spacing: 4px; text-transform: uppercase;
              margin: 0; font-weight: 800; }
  .title .no { font-size: 10pt; font-weight: 700; color: #0f766e; margin-top: 4px; }
  .title .due { font-size: 8.5pt; color: #64748b; margin-top: 2px; }
  hr { border: 0; border-top: 1px solid #e2e8f0; margin: 14px 0; }
  .cols { display: flex; gap: 32px; margin-bottom: 6px; }
  .cols > div { flex: 1; }
  .lbl { font-size: 7.5pt; font-weight: 800; letter-spacing: 1px; color: #94a3b8;
         text-transform: uppercase; margin-bottom: 6px; }
  .party { font-size: 11pt; font-weight: 700; }
  .domain { font-family: "Consolas", monospace; font-size: 9.5pt; color: #475569; margin-top: 3px; }
  .kv { display: flex; justify-content: space-between; padding: 2px 0; }
  .kv .k { font-size: 8.5pt; color: #64748b; }
  .kv .v { font-size: 9.5pt; font-weight: 600; }
  table { width: 100%; border-collapse: collapse; margin-top: 8px; }
  th { font-size: 7.5pt; font-weight: 800; letter-spacing: 1px; color: #94a3b8;
       text-transform: uppercase; text-align: left; padding-bottom: 6px;
       border-bottom: 2px solid #e2e8f0; }
  th.r, td.r { text-align: right; }
  td { padding: 12px 0; border-bottom: 1px solid #f1f5f9; vertical-align: top; }
  .desc { font-size: 10pt; font-weight: 600; }
  .sub { font-size: 8.5pt; color: #64748b; margin-top: 2px; }
  .amt { font-size: 10.5pt; font-weight: 700; }
  .totalwrap { display: flex; justify-content: flex-end; margin-top: 16px; }
  .total { min-width: 240px; display: flex; justify-content: space-between;
           align-items: center; background: #f8fafc; border: 1px solid #e2e8f0;
           border-radius: 5px; padding: 10px 14px; }
  .total .k { font-size: 9pt; font-weight: 700; color: #475569;
              text-transform: uppercase; letter-spacing: .5px; }
  .total .v { font-size: 14pt; font-weight: 800; color: #0f766e; }
  .notes { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 5px;
           padding: 10px 12px; margin-top: 20px; }
  .notes p { font-size: 9.5pt; color: #334155; margin: 4px 0 0; white-space: pre-wrap; }
  footer { margin-top: 26px; padding-top: 12px; border-top: 1px solid #e2e8f0;
           font-size: 8pt; color: #94a3b8; line-height: 1.7; }
  .ack { color: #2e8b57; font-weight: 600; margin-top: 6px; }
</style>
</head>
<body>
  <div class="strip">
    <span class="badge">${esc(meta.label)}</span>
    <span class="days">${esc(describeDays(data.daysLeft))}</span>
    <div class="tone">${esc(meta.tone)}</div>
  </div>

  <div class="masthead">
    <div class="brand">TotalCare HMS
      <small>Hospital Management System<br>Domain &amp; Hosting Services</small>
    </div>
    <div class="title">
      <h1>Invoice</h1>
      <div class="no">${esc(inv.invoiceNo)}</div>
      <div class="due">Due by ${esc(formatDate(inv.expiresAt))}</div>
    </div>
  </div>

  <hr>

  <div class="cols">
    <div>
      <div class="lbl">Billed to</div>
      <div class="party">${esc(inv.hospital)}</div>
      ${inv.domainName ? `<div class="domain">${esc(inv.domainName)}</div>` : ''}
    </div>
    <div>
      <div class="lbl">Registration</div>
      <div class="kv"><span class="k">Registrar</span><span class="v">${esc(inv.registrar || 'Namecheap Inc.')}</span></div>
      <div class="kv"><span class="k">Registered on</span><span class="v">${esc(formatDate(inv.registeredOn))}</span></div>
      <div class="kv"><span class="k">Paid through</span><span class="v">${esc(formatDate(inv.expiresAt))}</span></div>
      ${inv.graceUntil && inv.graceUntil !== inv.expiresAt
        ? `<div class="kv"><span class="k">Goes offline</span><span class="v" style="color:#c0392b">${esc(formatDate(inv.graceUntil))}</span></div>`
        : ''}
    </div>
  </div>

  <hr>

  <table>
    <thead><tr><th>Description</th><th class="r">Amount</th></tr></thead>
    <tbody>
      <tr>
        <td>
          <div class="desc">Domain registration renewal — ${inv.renewalYears > 1 ? esc(inv.renewalYears) + ' years' : '1 year'}</div>
          <div class="sub">${esc(inv.domainName || 'Facility domain')} · held with ${esc(inv.registrar || 'Namecheap Inc.')}</div>
          <div class="sub">Extends service from ${esc(formatDate(inv.expiresAt))}</div>
        </td>
        <td class="r amt">${esc(formatMoney(inv.renewalAmount, inv.currency))}</td>
      </tr>
    </tbody>
  </table>

  <div class="totalwrap">
    <div class="total">
      <span class="k">Total due</span>
      <span class="v">${esc(formatMoney(inv.renewalAmount, inv.currency))}</span>
    </div>
  </div>

  ${inv.billingNotes ? `
  <div class="notes">
    <div class="lbl">Payment instructions</div>
    <p>${esc(inv.billingNotes)}</p>
  </div>` : ''}

  <footer>
    Issued by TotalCare HMS for domain and hosting services rendered to the facility named above.
    The domain is registered and maintained on the facility's behalf with ${esc(inv.registrar || 'Namecheap Inc.')}.
    Renewal must be settled before the paid-through date to avoid interruption of access.
    ${data.acknowledged && data.acknowledgedBy
      ? `<div class="ack">Reviewed by ${esc(data.acknowledgedBy)}${data.acknowledgedAt ? ' on ' + esc(formatDate(data.acknowledgedAt)) : ''}</div>`
      : ''}
  </footer>

  <script>
    window.onload = function () { window.print(); };
  </script>
</body>
</html>`;

  const win = window.open('', '_blank', 'width=920,height=1000');
  if (!win) {
    // Pop-up blocked — tell the caller so it can surface a message.
    return false;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
  return true;
};

export default DomainRenewalInvoice;
